import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from '../../services/api';
import PaperConferenceSelection from "./PaperConferenceSelection";
import PaperDocumentUpload from "./PaperDocumentUpload";
import PaperTitleAbstract from "./PaperTitleAbstract";
import PaperResearchClassification from "./PaperResearchClassification";
import PaperAuthorsWizard from "./PaperAuthorsWizard";
import PaperSubmissionStepper from "./PaperSubmissionStepper";

/**
 * PaperSubmission Component
 * 
 * Main component for academic paper submission with AI-enhanced 5-step wizard.
 * Handles the complete paper submission process including:
 * - Step 1: Conference and manuscript type selection
 * - Step 2: Document upload with AI text extraction
 * - Step 3: Title and abstract review with AI auto-completion
 * - Step 4: Research classification (MFS codes)
 * - Step 5: Authors management with AI-detected authors
 * 
 * Features:
 * - AI-powered document analysis and auto-completion
 * - Step-by-step navigation with validation
 * - Real-time form state management
 * - Document upload with automatic text extraction
 * - Author management with AI detection capabilities
 * - Form submission with loading states and error handling
 */
const PaperSubmission = () => {
  const [currentSection, setCurrentSection] = useState(1);
  const [conferences, setConferences] = useState([]);
  const [extractedInfo, setExtractedInfo] = useState(null);
  const [hasExtractedInfo, setHasExtractedInfo] = useState(false);
  const [navigationLoading, setNavigationLoading] = useState(false);
  const [submissionAttempted, setSubmissionAttempted] = useState(false);
  
  // Ref to store the document extraction function from PaperDocumentUpload
  const documentExtractionRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Step 1: Conference and Type Selection
    conferenceId: "",
    manuscriptType: "",
    
    // Step 2: Document Upload
    uploadedFile: null,
    uploadedFileName: "",
    
    // Step 3: Title and Abstract
    manuscriptTitle: "",
    manuscriptAbstract: "",
    
    // Step 4: Research Classification
    researchArea1: "",
    researchArea1Category: "",
    researchArea2: "",
    researchArea2Category: "",
    researchArea3: "",
    researchArea3Category: "",
    researchArea4: "",
    researchArea4Category: "",
    researchArea5: "",
    researchArea5Category: "",
    
    // Step 5: Authors
    paperAuthors: [],
    autoAddedAuthors: false, // Track if authors have been auto-added
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  const normalizeTitle = (text) => text
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  const normalizeIfAllCaps = (title) => {
    const trimmedTitle = String(title || '').trim();
    return trimmedTitle && trimmedTitle === trimmedTitle.toUpperCase()
      ? normalizeTitle(trimmedTitle)
      : trimmedTitle;
  };

  useEffect(() => {
    // Cleanup function to prevent memory leaks
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    /**
     * Loads initial data required for paper submission
     * - Active conferences for step 1 selection
     */
    const loadInitialData = async () => {
      try {
        // Get active conferences for step 1
        const conferencesResponse = await api.get('/conferences/open');
        if (mountedRef.current) {
          setConferences(conferencesResponse.data || []);
        }
      } catch (error) {
        console.error("Error loading conference information:", error);
        if (mountedRef.current) {
          setError("Error loading conference information. Please refresh the page and try again.");
        }
      }
    };

    loadInitialData();
  }, []);

  // Clear errors when validation passes
  useEffect(() => {
    if (error && currentSection === 5 && validateSection(5)) {
      setError("");
    }
  }, [formData.paperAuthors, formData.currentUserInAuthors, error, currentSection]);

  /**
   * Handle form field changes
   * @param {Object} e - Event object with target name and value
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      };
      
      // Clear error if current section validation passes after this update
      setTimeout(() => {
        if (validateSection(currentSection)) {
          setError("");
        }
        // Also clear error if final validation (section 5) passes and we're on the authors step
        if (currentSection === 5 && validateSection(5)) {
          setError("");
        }
      }, 0);
      
      return updated;
    });
  };  /**
   * Normalize the manuscript title after editing completes
   */
  const handleTitleBlur = () => {
    setFormData(prev => {
      const normalizedTitle = normalizeIfAllCaps(prev.manuscriptTitle);
      return normalizedTitle === prev.manuscriptTitle
        ? prev
        : { ...prev, manuscriptTitle: normalizedTitle };
    });
  };

  /**
   * Handles extracted information from AI document processing
   * @param {Object|null} extracted - Extracted information from document, or null to clear
   */
  const handleExtractedInfo = (extracted) => {
    if (extracted === null) {
      // Clear all extracted information when null is passed (new file upload)
      setExtractedInfo(null);
      setHasExtractedInfo(false);
      
      // Clear all extracted content from form data
      setFormData(prev => ({
        ...prev,
        manuscriptTitle: "",
        manuscriptAbstract: "",
        paperAuthors: [],
        autoAddedAuthors: false,
        currentUserInAuthors: false
      }));
      
      return;
    }
    
    // Clear previous extracted content before setting new information
    setFormData(prev => ({
      ...prev,
      manuscriptTitle: "",
      manuscriptAbstract: "",
      // Keep the uploaded file information but clear extracted content
    }));
    
    setExtractedInfo(extracted);
    setHasExtractedInfo(true);
    
    // Auto-populate title and abstract if extracted from new document
    if (extracted.extractedInfo?.title) {
      setFormData(prev => ({ ...prev, manuscriptTitle: normalizeIfAllCaps(extracted.extractedInfo.title) }));
    }
    if (extracted.extractedInfo?.abstract) {
      setFormData(prev => ({ ...prev, manuscriptAbstract: extracted.extractedInfo.abstract }));
    }
  };

  /**
   * Handle setting the extraction function reference from PaperDocumentUpload
   * @param {Function} extractionFunction - The extraction function from the child component
   */
  const handleAutoExtract = (extractionFunction) => {
    documentExtractionRef.current = extractionFunction;
  };

  /**
   * Validates the current section's required fields
   * @param {number} section - Section number to validate (1-5)
   * @returns {boolean} True if section is valid, false otherwise
   */
  const validateSection = (section) => {
    switch (section) {
      case 1:
        const step1Valid = formData.conferenceId && formData.manuscriptType;
        return step1Valid;
      case 2:
        const step2Valid = formData.uploadedFile;
        return step2Valid;
      case 3:
        const step3Valid = formData.manuscriptTitle && formData.manuscriptAbstract;
        return step3Valid;
      case 4:
        const step4Valid = formData.researchArea1 && formData.researchArea2 && formData.researchArea3;
        return step4Valid;
      case 5:
        // Validate that there's at least one author and they have required fields
        const authors = formData.paperAuthors || [];
        
        if (authors.length === 0) {
          return false;
        }
        
        const hasRequiredFields = authors.every(author => 
          author.firstName && author.lastName && author.email && author.institution
        );
        
        const hasCorrespondingAuthor = authors.some(author => author.isCorresponding);
        
        // Check if current user is in the authors list by looking at the actual authors
        // Use the flag if it's explicitly set, otherwise check if any author is marked as current user
        let currentUserInAuthors = false;
        if (formData.currentUserInAuthors === true) {
          currentUserInAuthors = true;
        } else {
          // Fallback: check if any author has the isCurrentUser flag
          currentUserInAuthors = authors.some(author => author.isCurrentUser === true);
        }
        
        const step5Valid = hasRequiredFields && hasCorrespondingAuthor && currentUserInAuthors;
        return step5Valid;
      default:
        return true;
    }
  };

  /**
   * Advances to the next section after validation
   * Handles automatic AI extraction when leaving step 2
   * Scrolls to top of page for better user experience
   */
  const nextSection = async () => {
    
    const isValid = validateSection(currentSection);
    
    if (isValid) {
      const nextSectionNumber = currentSection + 1;
      setNavigationLoading(true);
      
      try {
        // Handle automatic extraction when leaving step 2 (document upload)
        if (currentSection === 2 && documentExtractionRef.current && !hasExtractedInfo) {
          const extractionSuccess = await documentExtractionRef.current();
          
          if (!extractionSuccess) {
            // Continue to next step even if extraction fails
          }
        }
        
        setCurrentSection(nextSectionNumber);
        setError("");
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
      } catch (error) {
        console.error("Error during navigation:", error);
        // Even if there's an error, allow navigation to continue
        setCurrentSection(nextSectionNumber);
        setError("");
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } finally {
        setNavigationLoading(false);
      }
    } else {
      setError("Please fill in all required fields correctly.");
    }
  };

  /**
   * Goes back to the previous section
   * Clears any existing errors and scrolls to top
   */
  const prevSection = () => {
    setCurrentSection(currentSection - 1);
    setError("");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Handles the final paper submission
   * Validates final section, prepares FormData with all submission data,
   * and sends to the server via API call
   * 
   * @param {Event} e - Form submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark that submission has been attempted
    setSubmissionAttempted(true);
    
    // Final validation before submission
    const isValid = validateSection(5);
    
    if (!isValid) {
      setError("Please ensure all required fields are completed and at least one corresponding author is selected.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Prepare FormData for multipart upload
      const submissionData = new FormData();
      
      // Add basic paper information
      submissionData.append('manuscriptType', formData.manuscriptType);
      submissionData.append('conferenceId', formData.conferenceId);
      submissionData.append('manuscriptTitle', normalizeIfAllCaps(formData.manuscriptTitle));
      submissionData.append('manuscriptAbstract', formData.manuscriptAbstract);
      submissionData.append('researchArea1', formData.researchArea1);
      submissionData.append('researchArea1Category', formData.researchArea1Category);
      submissionData.append('researchArea2', formData.researchArea2);
      submissionData.append('researchArea2Category', formData.researchArea2Category);
      submissionData.append('researchArea3', formData.researchArea3);
      submissionData.append('researchArea3Category', formData.researchArea3Category);
      submissionData.append('researchArea4', formData.researchArea4);
      submissionData.append('researchArea4Category', formData.researchArea4Category);
      submissionData.append('researchArea5', formData.researchArea5);
      submissionData.append('researchArea5Category', formData.researchArea5Category);
      
      // Add authors
      submissionData.append('authors', JSON.stringify(formData.paperAuthors));
      
      // Add the uploaded manuscript file
      if (formData.uploadedFile) {
        submissionData.append('fullManuscript', formData.uploadedFile);
      }

      // Submit to server
      await api.post('/papers/submit', submissionData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Show success message
      alert('🎉 Paper submitted successfully!\n\nYou will be redirected to your submissions where you can track the status of your paper.');
      
      // Redirect to dashboard submissions tab with success indicator
      navigate('/dashboard?tab=submissions&submitted=true');
      
    } catch (err) {
      console.error('Submission error:', err);
      
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError("Submission Error: Please try again.");
      }
    } finally {
      // Check if component is still mounted before updating state
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  /**
   * Renders the current section based on the step in the submission process
   * @returns {JSX.Element} The appropriate section component
   */
  const renderCurrentSection = () => {
    switch (currentSection) {
      case 1:
        return (
          <PaperConferenceSelection
            formData={formData}
            handleChange={handleChange}
            conferences={conferences}
          />
        );
      case 2:
        return (
          <PaperDocumentUpload
            formData={formData}
            handleChange={handleChange}
            onExtractedInfo={handleExtractedInfo}
            extractedInfo={extractedInfo}
            hasExtractedInfo={hasExtractedInfo}
            onAutoExtract={handleAutoExtract}
          />
        );
      case 3:
        return (
          <PaperTitleAbstract
            formData={formData}
            handleChange={handleChange}
            handleTitleBlur={handleTitleBlur}
            extractedInfo={extractedInfo}
            hasExtractedInfo={hasExtractedInfo}
          />
        );
      case 4:
        return (
          <PaperResearchClassification
            formData={formData}
            handleChange={handleChange}
          />
        );
      case 5:
        return (
          <PaperAuthorsWizard
            formData={formData}
            handleChange={handleChange}
            extractedInfo={extractedInfo}
            hasExtractedInfo={hasExtractedInfo}
            submissionAttempted={submissionAttempted}
          />
        );
      default:
        return (
          <PaperConferenceSelection
            formData={formData}
            handleChange={handleChange}
            conferences={conferences}
          />
        );
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-12 col-lg-10">
          <div className="card shadow">
            <div className="card-header">
              <h2 className="mb-0">
                <i className="bi bi-file-earmark-text me-2"></i>
                Step {currentSection} of 5 - Manuscript Submission
              </h2>
            </div>

            {/* Progress Bar */}
            <div className="progress" style={{ height: "4px", borderRadius: 0 }}>
              <div
                className="progress-bar"
                style={{ width: `${(currentSection / 4) * 100}%` }}
              ></div>
            </div>

            <div className="card-body p-4">
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <p>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                    <br/>
                    We encountered an error while processing your submission. Please try again.
                  </p>
                  <p>
                    Please review the form for missing or incorrect entries and try again. 
                    <br />
                    If the issue persists, contact us at mfs@mfsociety.org for assistance.
                  </p>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError("")}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {renderCurrentSection()}

                {/* Navigation Controls */}
                <PaperSubmissionStepper
                  currentSection={currentSection}
                  prevSection={prevSection}
                  nextSection={nextSection}
                  handleSubmit={handleSubmit}
                  loading={loading}
                  navigationLoading={navigationLoading}
                />
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaperSubmission;
